import { authService } from '@/lib/authService';

/**
 * Manual tour-media regeneration (coach audio + recorded video).
 *
 * Dev-only + root-only: the button surfaces only on the development portal
 * for root users, and the API enforces both checks again server-side.
 * Nothing regenerates on a schedule — media changes only on an explicit
 * request, so approved videos/audio are never silently replaced.
 */

export type TourMediaRegenKind = 'audio' | 'video' | 'all';

/**
 * Window event the media manager listens for. Dispatched with a
 * `{ moduleKey, flowId? }` detail — the provider whose moduleKey matches
 * opens its manager modal.
 */
export const TOUR_MEDIA_MANAGER_EVENT = 'guided-tour-media-manager-open';

export interface TourMediaManagerEventDetail {
  moduleKey?: string;
  /** Optional: expand this flow when the manager opens. */
  flowId?: string;
}

export function openTourMediaManager(detail: TourMediaManagerEventDetail = {}): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TOUR_MEDIA_MANAGER_EVENT, { detail }));
}

export function isTourMediaRegenAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  // Dev AND prod portals: prod routes proxy to the dev control plane, so
  // root users can trigger dev-side media work from either env. The server
  // still enforces root on both hops.
  return authService.getUser()?.role === 'root';
}

export async function requestTourMediaRegen(input: {
  moduleKey: string;
  flowId?: string;
  /** Narrow to specific steps (audio only) — e.g. the inline step editor. */
  stepIds?: string[];
  kind?: TourMediaRegenKind;
  /**
   * Default true — re-synthesize even when hashes match. Pass false for a
   * "fill missing / refresh stale only" sweep.
   */
  force?: boolean;
}): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  jobIds?: { audio?: string; video?: string };
  deduplicated?: { audio?: { id: string; deduplicated: boolean }; video?: { id: string; deduplicated: boolean } };
}> {
  const token = authService.getTokens()?.access_token;
  const response = await fetch('/api/v1/guided-tours/regenerate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      moduleKey: input.moduleKey,
      ...(input.flowId ? { flowId: input.flowId } : {}),
      ...(input.stepIds?.length ? { stepIds: input.stepIds } : {}),
      kind: input.kind ?? 'all',
      force: input.force !== false,
    }),
  });
  const result = await response.json().catch(() => ({}));
  return {
    success: response.ok && result?.success === true,
    message: result?.message,
    error: result?.error || (response.ok ? undefined : `HTTP ${response.status}`),
    jobIds: result?.jobIds,
    deduplicated: result?.deduplicated,
  };
}

// ── Media status (GET /api/v1/guided-tours/media-status) ────────────────────

export type TourAudioEntryStatus = 'none' | 'missing' | 'stale' | 'ready';

export interface TourAudioStatusEntry {
  status: TourAudioEntryStatus;
  staleReason?: 'text-changed' | 'old-gen';
  /** Whether users would actually hear this WAV (hash-match attach rule). */
  serves?: boolean;
  audioUrl?: string;
  durationSeconds?: number;
  generatedAt?: string;
  sizeBytes?: number;
  voice?: string;
}

export interface TourVideoStatusEntry {
  status: 'missing' | 'stale' | 'ready';
  videoUrl?: string;
  thumbnailUrl?: string;
  durationSeconds?: number;
  generatedAt?: string;
  sizeBytes?: number;
}

export interface TourMediaJobInfo {
  id: string;
  name: string;
  state: string;
  progress: { done?: number; total?: number } | number | null;
  moduleKey?: string;
  flowId?: string;
  stepIds?: string[];
  force?: boolean;
  triggeredBy?: string;
  createdAt?: number;
  processedOn?: number;
  finishedOn?: number;
  result?: { checked?: number; generated?: number; rendered?: number; skipped?: number; failed?: number; errors?: string[] } | null;
  failedReason?: string;
}

export interface TourMediaTargetStatus {
  kind: 'focus' | 'reveal' | 'verify' | string;
  id: string;
  /** 'found' = resolves to a data-tour-id in source; 'missing' = nothing renders it. */
  status: 'found' | 'missing' | 'none' | string;
}

export interface TourMediaStepStatus {
  id: string;
  title: string;
  mascot_pose: string | null;
  mascot_position: string | null;
  narrated: boolean;
  narration: TourAudioStatusEntry;
  titleAudio: TourAudioStatusEntry;
  targets: TourMediaTargetStatus[];
}

export interface TourMediaFlowStatus {
  id: string;
  label: string;
  target_tab: string | null;
  /** 'desktop' for the base file, otherwise the supplement file key (e.g. 'total-operations-mobile'). */
  sourceFile: string;
  intro: TourAudioStatusEntry;
  video: TourVideoStatusEntry;
  steps: TourMediaStepStatus[];
}

export interface TourMediaStatus {
  success: boolean;
  moduleKey: string;
  moduleName: string;
  audioDirAvailable: boolean;
  videoDirAvailable: boolean;
  audioManifestGeneratedAt: string | null;
  stats: {
    flows: number;
    steps: number;
    narratedSteps: number;
    audio: Record<TourAudioEntryStatus, number>;
    videos: Record<'ready' | 'stale' | 'missing', number>;
    /** Steps' spotlight/verify selectors that resolve to nothing in source. */
    missingTargets: number;
  };
  flows: TourMediaFlowStatus[];
  jobs: TourMediaJobInfo[];
  error?: string;
}

/** Full inventory — YAML scan + WAV probes. A few hundred ms to ~2s. */
export async function fetchTourMediaStatus(moduleKey: string): Promise<TourMediaStatus | null> {
  const token = authService.getTokens()?.access_token;
  const response = await fetch(
    `/api/v1/guided-tours/media-status?moduleKey=${encodeURIComponent(moduleKey)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  const result = await response.json().catch(() => null);
  return result?.success ? (result as TourMediaStatus) : null;
}

/** Cheap job-only poll — used every few seconds while a run is active. */
export async function fetchTourMediaJobs(moduleKey: string): Promise<TourMediaJobInfo[]> {
  const token = authService.getTokens()?.access_token;
  const response = await fetch(
    `/api/v1/guided-tours/media-status?moduleKey=${encodeURIComponent(moduleKey)}&jobsOnly=1`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  const result = await response.json().catch(() => null);
  return Array.isArray(result?.jobs) ? (result.jobs as TourMediaJobInfo[]) : [];
}

/**
 * Set a flow's narration voice — PATCHes `tts.voice` into the module YAML via
 * the scoped dev+root patch route. The worker/media-status/serve paths all
 * resolve step.tts_voice → flow.tts.voice → 'af_heart', so a new voice re-keys
 * the manifest lookups: every un-overridden step shows "missing" until the
 * user regenerates.
 */
export async function setTourFlowVoice(input: {
  moduleKey: string;
  flowId: string;
  voice: string;
}): Promise<{ success: boolean; error?: string }> {
  const token = authService.getTokens()?.access_token;
  const response = await fetch(
    `/api/v1/guided-tours/modules/${encodeURIComponent(input.moduleKey)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        flowId: input.flowId,
        patch: { tts: { voice: input.voice } },
      }),
    },
  );
  const json = await response.json().catch(() => ({}));
  if (!response.ok || !json.success) {
    return { success: false, error: json.error || 'Failed to save voice' };
  }
  return { success: true };
}

/**
 * Queue controls on a tour-media job — PATCH 'cancel' (removes waiting jobs,
 * flags active ones for cooperative stop) or 'retry' (re-queues failed/done),
 * DELETE removes the record entirely. Mirrors the render-queue controls.
 */
export async function tourMediaJobAction(
  jobId: string,
  action: 'cancel' | 'retry' | 'delete',
): Promise<{ success: boolean; error?: string }> {
  const token = authService.getTokens()?.access_token;
  const response = await fetch(`/api/v1/guided-tours/media-status/${jobId}`, {
    method: action === 'delete' ? 'DELETE' : 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(action === 'delete' ? {} : { body: JSON.stringify({ action }) }),
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json.success === false) {
    return { success: false, error: json.error || `HTTP ${response.status}` };
  }
  return { success: true };
}
